import { observer } from 'mobx-react';
import { useInjection } from 'inversify-react';
import { TYPES } from '@/engine/ioc';
import { ImageViewPortWrapper } from '../../views/ImageViewPortWrapper';
import { SymmetricalQuadroLayout } from './SymmetricalQuadroLayout';
import { ThreeDViewPortWrapper } from '../../views/GridThreeDViewPortWrapper';
import { PointsSwitcher } from '../../../PointsSwitcher/PointsSwitcher';
import styled from 'styled-components';
import { StepsList } from '../../../stepsList/StepsList';
import type { SymmetricalQuadroViewLayoutController } from './SymmetricalQuadroViewLayoutController';
import { CineloopHover } from '@/components/loading-animation/CineloopHover';

export interface ViewPortProps extends React.HTMLAttributes<HTMLDivElement> {
  descriptor: string;
  bgColor: number;
}

export const QUADRO_LAYOUT_PLACEHOLDERS = ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'] as const;

export const SymmetricalQuadroViewLayout: React.FC = observer(() => {
  const controller = useInjection<SymmetricalQuadroViewLayoutController>(TYPES.SymmetricalQuadroViewLayoutController);

  const imagesContent = controller.imageViews.map((iv) => {
    const placeholderDescriptor = controller.getLayoutPlaceholderDescriptorByContentDescriptor(iv.descriptor);
    if (!placeholderDescriptor) return null;
    return {
      placeholderDescriptor: placeholderDescriptor,
      content: (
        <ContentWrapper style={{ borderColor: iv.borderColor }}>
          <ImageViewPortWrapper
            className="image-viewport-wrapper" // [namazov]: legacy. internal buttons rely on this class
            imageView={iv}
            isMaximized={controller.maximizedViewDescriptor === placeholderDescriptor}
            maximize={(): void => {
              controller.maximizeView(placeholderDescriptor);
            }}
            minimize={(): void => {
              controller.resetMaximization();
            }}
            clippingManager={controller.clippingManager}
            key={iv.descriptor}
          />
        </ContentWrapper>
      ),
      borderColor: iv.borderColor
    };
  });

  const threeDContent = controller.threeDViews.map((td) => {
    const placeholderDescriptor = controller.getLayoutPlaceholderDescriptorByContentDescriptor(td.descriptor);
    if (!placeholderDescriptor) return null;
    return {
      placeholderDescriptor: placeholderDescriptor,
      content: (
        <ThreeDViewPortWrapper
          isMaximized={controller.maximizedViewDescriptor === placeholderDescriptor}
          onMaximize={(): void => {
            controller.maximizeView(placeholderDescriptor);
          }}
          onMinimize={(): void => {
            controller.resetMaximization();
          }}
          key={td.descriptor}
          threeDView={td}
          viewOrientationDescriptor={controller.viewOrientationDescriptor}
          helpers={controller.helpers}
          isCollapsed={false}
        />
      ),
      borderColor: 'white'
    };
  });

  const descriptor2ContentMap: Record<string, { content: React.ReactNode; borderColor: string }> = [
    imagesContent,
    threeDContent
  ]
    .flat()
    .reduce(
      (acc, cur) => {
        if (!cur?.placeholderDescriptor) return acc;
        acc[cur.placeholderDescriptor] = { content: cur.content, borderColor: cur.borderColor };
        return acc;
      },
      {} as Record<string, { content: React.ReactNode; borderColor: string }>
    );

  // TODO: [namazov]: Points switcher is surgery-specific, it shouldn't be here
  const mainContent = (
    <MainContentContainer>
      {descriptor2ContentMap['bottomRight'].content}
      <ButtonsContainer>
        <PointsSwitcher />
      </ButtonsContainer>
    </MainContentContainer>
  );

  const footerContent = (
    <StepsContainer data-testid="stepsContainer" className="steps-container">
      <CineloopHover />
      <StepsList />
    </StepsContainer>
  );

  // [namazov]: originally it was hardcoded to quadro layout, so I left it as is. Consider generalizing it
  return (
    <SymmetricalQuadroLayout
      topLeftContent={descriptor2ContentMap['topLeft'].content}
      topRightContent={descriptor2ContentMap['topRight'].content}
      bottomLeftContent={descriptor2ContentMap['bottomLeft'].content}
      bottomRightContent={mainContent}
      footerContent={footerContent}
      maximizedArea={controller.maximizedViewDescriptor}
    />
  );
});

const ButtonsContainer = styled.div`
  position: relative;
  margin-left: 1.5rem;
  pointer-events: none;
`;

const MainContentContainer = styled.div`
  height: 100%;
`;

const StepsContainer = styled.div`
  position: relative;
  background-color: ${({ theme }): string => theme.customPalette.panels.stepper.background};
  display: flex;
  align-items: center;
  overflow: hidden;
  pointer-events: none;
  min-height: 40px;
`;

const ContentWrapper = styled.div`
  height: 100%;
  border: 4px solid transparent;
`;
